import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SchoolIcon from '@mui/icons-material/School';
import BusinessIcon from '@mui/icons-material/Business';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CircularProgress from '@mui/material/CircularProgress';
import { copyToClipboard } from '../../../utils/clipboard';
import { fetchAllPaginatedItems, PaginatedFetchError } from '../../../utils/fetchPaginated';
import { useToast } from '../../../contexts/ToastContext';
import { formatDateMsk, parseApiDateTime } from '../../../utils/date';

import FilterSortPanel from '../../ui/FilterSortPanel';
import { AppPagination } from '../../ui/AppPagination';

import StudentExamsModal from './StudentExamsModal';
import { StudentFullInfoModal } from '../ApplicationList/components/StudentFullInfoModal';

interface ApiDictItem {
  id: number;
  full_name: string;
  short_name?: string;
}

interface ApiApplicationSimple {
  id: number;
  organization_id: number;
}

interface NestedProgram {
  id: number;
  name: string;
  id_in_program?: string;
  duration_hours?: number | null;
}

interface NestedStudentProgram {
  student_id: number;
  program_id: number;
  status: string;
  start_date: string;
  end_date?: string | null;
  exam_date?: string | null;
  program: NestedProgram;
}

interface StudentItem {
  id: number;
  application_id: number;
  full_name: string;
  email: string | null;
  login: string | null;
  password: string | null;
  is_active: boolean;
  student_programs: NestedStudentProgram[];
}

interface StudentsApiResponse {
  total: number;
  page: number;
  size: number;
  items: StudentItem[];
}

interface StudentSearchRequest {
  organization_name?: string | null;
  student_query?: string | null;
  application_id?: number | null;
  start_date_from?: string | null;
  start_date_to?: string | null;
  end_date_from?: string | null;
  end_date_to?: string | null;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

interface AppContextData {
  id: number;
  status: string;
  organization: { name: string };
  specialist: { name: string };
}

const StudentList: React.FC = () => {
  const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1`;
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [students, setStudents] = useState<StudentItem[]>([]);
  const [orgMap, setOrgMap] = useState<Map<number, string>>(new Map());
  const [appToOrgMap, setAppToOrgMap] = useState<Map<number, number>>(new Map());

  const [loading, setLoading] = useState<boolean>(true);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);

  const [organizationSearch, setOrganizationSearch] = useState<string>('');
  const [studentSearch, setStudentSearch] = useState<string>('');
  const [applicationIdSearch, setApplicationIdSearch] = useState<string>('');
  const [startDateFrom, setStartDateFrom] = useState<string | null>(null);
  const [startDateTo, setStartDateTo] = useState<string | null>(null);
  const [endDateFrom, setEndDateFrom] = useState<string | null>(null);
  const [endDateTo, setEndDateTo] = useState<string | null>(null);

  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [appliedFilters, setAppliedFilters] = useState<StudentSearchRequest>({
    organization_name: null,
    student_query: null,
    application_id: null,
    start_date_from: null,
    start_date_to: null,
    end_date_from: null,
    end_date_to: null,
  });

  // Вычисляем, активен ли хотя бы один фильтр, поиск или сортировка, отличная от дефолтной
  const isResetDisabled =
    !studentSearch &&
    !organizationSearch &&
    !applicationIdSearch &&
    startDateFrom === null &&
    startDateTo === null &&
    endDateFrom === null &&
    endDateTo === null &&
    sortBy === 'created_at' &&
    sortOrder === 'desc' &&
    appliedFilters.organization_name === null &&
    appliedFilters.student_query === null &&
    appliedFilters.application_id === null &&
    appliedFilters.start_date_from === null &&
    appliedFilters.start_date_to === null &&
    appliedFilters.end_date_from === null &&
    appliedFilters.end_date_to === null;

  const [isExamModalOpen, setIsExamModalOpen] = useState<boolean>(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentItem | null>(null);
  const [selectedStudentForInfo, setSelectedStudentForInfo] = useState<StudentItem | null>(null);
  const [appContextForModal, setAppContextForModal] = useState<AppContextData | null>(null);
  const [loadingFullInfoId, setLoadingFullInfoId] = useState<number | null>(null);

  const token = localStorage.getItem('token');

  const handleLogout = useCallback(() => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    const fetchDictionaries = async () => {
      if (!token) return;
      try {
        const headers = {
          "Accept": "application/json",
          "Authorization": token
        };
        const [appsData, orgsData] = await Promise.all([
          fetchAllPaginatedItems<ApiApplicationSimple>(`${API_BASE_URL}/applications/`, headers),
          fetchAllPaginatedItems<ApiDictItem>(`${API_BASE_URL}/organizations/`, headers),
        ]);

        setOrgMap(new Map(orgsData.map((o) => [o.id, o.short_name || o.full_name])));
        setAppToOrgMap(new Map(appsData.map((a) => [a.id, a.organization_id])));
      } catch (error) {
        if (error instanceof PaginatedFetchError && error.status === 401) {
          handleLogout();
          return;
        }
        console.error("Error fetching dictionaries:", error);
      }
    };
    fetchDictionaries();
  }, [API_BASE_URL, token, handleLogout]);

  const fetchStudents = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: pageSize.toString(),
      });
      const response = await fetch(`${API_BASE_URL}/students/search?${queryParams.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({ ...appliedFilters, sort_by: sortBy, sort_order: sortOrder })
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }
      if (!response.ok) throw new Error(`Error: ${response.statusText}`);

      const data: StudentsApiResponse = await response.json();
      setStudents(data.items || []);
      setTotalItems(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch students:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedFilters, sortBy, sortOrder, token, handleLogout, API_BASE_URL]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleOpenExamModal = (student: StudentItem) => {
    setSelectedStudent(student);
    setIsExamModalOpen(true);
  };

  const handleCloseExamModal = () => {
    setIsExamModalOpen(false);
    setSelectedStudent(null);
  };

  const handleOpenFullInfo = async (student: StudentItem) => {
    setLoadingFullInfoId(student.id);
    try {
      const response = await fetch(`${API_BASE_URL}/applications/${student.application_id}`, {
        headers: {
          "Accept": "application/json",
          "Authorization": token || ''
        }
      });

      if (response.ok) {
        const appData = await response.json();
        const context: AppContextData = {
          id: appData.id,
          status: appData.status,
          organization: {
            name: appData.organization?.short_name || appData.organization?.full_name || 'Неизвестная организация'
          },
          specialist: {
            name: appData.responsible_specialist?.full_name || 'Не назначен'
          }
        };
        setAppContextForModal(context);
        setSelectedStudentForInfo(student);
      } else {
        setAppContextForModal({
          id: student.application_id,
          status: 'Не удалось загрузить',
          organization: { name: getOrganizationName(student.application_id) },
          specialist: { name: '—' }
        });
        setSelectedStudentForInfo(student);
      }
    } catch (error) {
      console.error("Error loading full info:", error);
    } finally {
      setLoadingFullInfoId(null);
    }
  };

  const handleCloseFullInfo = () => {
    setSelectedStudentForInfo(null);
    setAppContextForModal(null);
  };

  const handleSearchClick = () => {
    setPage(1);
    setAppliedFilters({
      organization_name: organizationSearch || null,
      student_query: studentSearch || null,
      application_id: applicationIdSearch ? Number(applicationIdSearch) : null,
      start_date_from: startDateFrom,
      start_date_to: startDateTo,
      end_date_from: endDateFrom,
      end_date_to: endDateTo,
    });
  };

  const handleReset = () => {
    setStudentSearch('');
    setOrganizationSearch('');
    setApplicationIdSearch('');
    setStartDateFrom(null);
    setStartDateTo(null);
    setEndDateFrom(null);
    setEndDateTo(null);
    setSortBy('created_at');
    setSortOrder('desc');
    setPage(1);
    setAppliedFilters({
      organization_name: null,
      student_query: null,
      application_id: null,
      start_date_from: null,
      start_date_to: null,
      end_date_from: null,
      end_date_to: null,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearchClick();
  };

  const getOrganizationName = (applicationId: number) => {
    if (appToOrgMap.size === 0) return 'Загрузка...';
    const orgId = appToOrgMap.get(applicationId);
    if (!orgId) return 'Организация не найдена';
    return orgMap.get(orgId) || 'Неизвестная организация';
  };

  const handleCopyCredentials = async (login: string, pass: string) => {
    const success = await copyToClipboard(`Логин: ${login}\nПароль: ${pass}`);
    if (success) {
      addToast({
        message: 'Логин и пароль успешно скопированы!',
        type: 'success'
      });
    } else {
      addToast({
        message: 'Не удалось скопировать данные',
        type: 'error'
      });
    }
  };

  const formatDatesList = (dates: (string | null | undefined)[]) => {
    const validDates = dates.filter((d): d is string => !!d);
    if (validDates.length === 0) return "—";
    const uniqueDates = Array.from(new Set(validDates));
    uniqueDates.sort((a, b) => parseApiDateTime(a).getTime() - parseApiDateTime(b).getTime());
    return uniqueDates.map(d => formatDateMsk(d) || '—').join("; ");
  };

  const visibleStudents = students.length > pageSize
    ? students.slice((page - 1) * pageSize, page * pageSize)
    : students;

  return (
    <div className="w-full bg-white pb-10">
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">

        {/* Заголовок */}
        <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-3">
          <SchoolIcon fontSize="large" className="text-blue-600" />
          Студенты
        </h1>
        </div>

        {/* Блок фильтров с четкой границей */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-6 relative z-20 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-white w-full">
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Организация</label>
                  <input
                    type="text"
                    value={organizationSearch}
                    onChange={(e) => setOrganizationSearch(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Название..."
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Студент</label>
                  <input
                    type="text"
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="ФИО, логин..."
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ID заявки</label>
                  <input
                    type="text"
                    value={applicationIdSearch}
                    onChange={(e) => /^\d*$/.test(e.target.value) && setApplicationIdSearch(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="ID..."
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-2 border-t border-gray-100">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Начало обучения (С)</label>
                  <input type="date" value={startDateFrom ?? ''} onChange={(e) => setStartDateFrom(e.target.value || null)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Начало обучения (По)</label>
                  <input type="date" value={startDateTo ?? ''} onChange={(e) => setStartDateTo(e.target.value || null)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Окончание обучения (С)</label>
                  <input type="date" value={endDateFrom ?? ''} onChange={(e) => setEndDateFrom(e.target.value || null)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Окончание обучения (По)</label>
                  <input type="date" value={endDateTo ?? ''} onChange={(e) => setEndDateTo(e.target.value || null)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm" />
                </div>
              </div>
            </div>
          </div>

          <FilterSortPanel
            showSearch={false}
            searchTerm=""
            onSearchChange={() => {}}
            onSearchSubmit={() => {}}
            showDateRange={false}
            sortOptions={[{ value: 'created_at', label: 'Дата создания' }]}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            sortOrder={sortOrder}
            onSortOrderChange={setSortOrder}
          />

          {/* Панель управления с кнопками, оформленными как в ApplicationList */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={handleReset}
              disabled={loading || isResetDisabled}
              className={`px-6 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
                isResetDisabled
                  ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed opacity-70'
                  : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-100 shadow-sm'
              }`}
            >
              Сбросить
            </button>
            <button
              onClick={handleSearchClick}
              disabled={loading}
              className={`px-6 py-2 rounded-lg text-white text-sm font-semibold transition-all duration-200 shadow-sm ${
                loading
                  ? 'bg-blue-600 opacity-50 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              Найти
            </button>
          </div>
        </div>

        {/* Список карточек */}
        <div className="min-h-[200px]">
          {loading ? (
            <div className="flex justify-center items-center w-full py-20">
              <CircularProgress />
            </div>
          ) : students.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl flex flex-col items-center justify-center py-16 px-6 text-gray-500">
              <SchoolIcon style={{ fontSize: 48, opacity: 0.3 }} />
              <p className="text-lg mt-4 font-semibold">Студенты не найдены</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {visibleStudents.map((student) => {
                const orgName = getOrganizationName(student.application_id);
                const isThisStudentLoading = loadingFullInfoId === student.id;

                return (
                  <div key={student.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-all duration-200">
                    <div className="p-5">
                      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-2">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-800 leading-tight">
                            {student.full_name || 'Без имени'}
                          </h3>
                          <div className="text-sm text-gray-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span>{student.email || <span className="italic text-gray-400">Email не указан</span>}</span>
                            <span className="text-gray-300 hidden sm:inline">|</span>
                            <span className="flex items-center gap-1 text-gray-600 font-medium">
                              <AssignmentIcon fontSize="inherit" className="text-gray-400" />
                              ID Заявки: {student.application_id}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-2 text-sm text-gray-700 font-medium">
                            <BusinessIcon fontSize="small" className="text-gray-400" />
                            {orgName}
                          </div>
                        </div>

                        <div className="flex gap-2 shrink-0 self-end sm:self-start">
                          <button
                            className="w-10 h-10 inline-flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition"
                            title="Просмотр"
                            onClick={() => handleOpenFullInfo(student)}
                            disabled={isThisStudentLoading}
                          >
                            {isThisStudentLoading ? <CircularProgress size={20} color="inherit" /> : <VisibilityIcon />}
                          </button>
                          <button
                            onClick={() => handleOpenExamModal(student)}
                            className="w-10 h-10 inline-flex items-center justify-center text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-full transition"
                            title="Статус / Экзамены"
                          >
                            <CheckCircleOutlineIcon />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50 p-3 rounded-lg border border-gray-100 text-sm mb-4 mt-2">
                        <div>
                          <span className="block text-xs text-gray-400 uppercase font-semibold mb-0.5">Дата начала обучения</span>
                          <span className="font-medium text-gray-800">{formatDatesList(student.student_programs.map(p => p.start_date))}</span>
                        </div>
                        <div>
                          <span className="block text-xs text-gray-400 uppercase font-semibold mb-0.5">Дата окончания обучения</span>
                          <span className="font-medium text-gray-800">{formatDatesList(student.student_programs.map(p => p.end_date))}</span>
                        </div>
                      </div>

                      {student.student_programs.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-1">
                          {student.student_programs.map((sp, idx) => (
                            <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                              {sp.program?.name || `ID: ${sp.program_id}`}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="bg-gray-50 px-5 py-3 border-t border-gray-200 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 font-medium text-xs uppercase">Логин:</span>
                        <span className="font-mono bg-white border border-gray-200 px-2 py-0.5 rounded text-blue-700 select-all">
                          {student.login || '—'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 font-medium text-xs uppercase">Пароль:</span>
                        <span className="font-mono bg-white border border-gray-200 px-2 py-0.5 rounded text-red-700 select-all">
                          {student.password || '—'}
                        </span>
                      </div>
                      {student.login && student.password && (
                        <button
                          onClick={() => handleCopyCredentials(student.login!, student.password!)}
                          className="ml-auto sm:ml-0 text-gray-500 hover:text-blue-700 transition flex items-center gap-1.5 text-xs font-medium bg-white hover:bg-blue-50 border border-gray-200 px-3 py-1 rounded-md shadow-sm"
                        >
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                          <span>Копировать данные</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Пагинация */}
        {!loading && students.length > 0 && (
          <div className="mt-6 bg-white border border-gray-200 rounded-xl px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
            <div className="text-sm text-gray-700">
              Показано <span className="font-medium">{totalItems > 0 ? (page - 1) * pageSize + 1 : 0}</span> - <span className="font-medium">{Math.min(page * pageSize, totalItems)}</span> из <span className="font-medium">{totalItems}</span>
            </div>
            <AppPagination page={page} totalItems={totalItems} pageSize={pageSize} onChange={setPage} />
          </div>
        )}
      </div>

      <StudentExamsModal isOpen={isExamModalOpen} onClose={handleCloseExamModal} student={selectedStudent} onLogout={handleLogout} />
      {selectedStudentForInfo && appContextForModal && (
        <StudentFullInfoModal student={{ ...selectedStudentForInfo, position: null }} applicationContext={appContextForModal} onBack={handleCloseFullInfo} />
      )}
    </div>
  );
};

export default StudentList;